/** @jsx isolateComponent */

import { ReactNode } from 'react';

import Link from '@zapier/design-system/src/components/navigation/Link';
import { kebabCaseAriaAttrs } from '@zapier/design-system/src/utils/formatAriaAttrKeys';
import {
  Animation,
  ColorValue,
  Colors,
  Typography,
} from '@zapier/design-system/src/theme';
import {
  hexToRrggbbaa,
  isolateComponent,
  labelStyles,
  responsiveStyles,
  withModifier,
} from '@zapier/common-utils';

type BadgeColorVariant = {
  base: {
    background: string;
    color: ColorValue;
  };
  hover: {
    background: string;
    color: ColorValue;
  };
};
const BadgeVariants = {
  light: {
    base: {
      background: hexToRrggbbaa(Colors.black, 0.05),
      color: Colors.black,
    },
    hover: {
      background: hexToRrggbbaa(Colors.blue100, 1),
      color: Colors.blue500,
    },
  },
  white: {
    base: {
      background: hexToRrggbbaa(Colors.white, 1),
      color: Colors.black,
    },
    hover: {
      background: hexToRrggbbaa(Colors.black, 0.05),
      color: Colors.blue500,
    },
  },
  grey: {
    base: {
      background: hexToRrggbbaa(Colors.black, 0.1),
      color: Colors.grey700,
    },
    hover: {
      background: hexToRrggbbaa(Colors.black, 0.1),
      color: Colors.grey700,
    },
  },
  dark: {
    base: {
      background: hexToRrggbbaa(Colors.white, 0.3),
      color: Colors.white,
    },
    hover: {
      background: hexToRrggbbaa(Colors.white, 0.2),
      color: Colors.white,
    },
  },
};

type BadgeProps = {
  /** The color variant of the badge. */
  color?: keyof typeof BadgeVariants;
  /** Overrides the Badge's color variant. Supercedes the `color` prop. */
  colorVariant?: BadgeColorVariant;
  /** Make badge clickable with link. */
  href?: string;
  /** Anchor link target. */
  hrefTarget?: '_self' | '_blank' | '_parent' | '_top';
  /** Text to render inside the badge. */
  children: ReactNode;
  /** Indicates the element represents the current item within a list. */
  ariaCurrent?: 'page' | 'step' | 'location' | 'date' | 'time' | true | false;
  /** The size of the badge to render. */
  size?: 'small' | 'large';
};

const defaultProps = {
  color: 'light' as keyof typeof BadgeVariants,
  size: 'small' as BadgeProps['size'] & string,
};

type BadgePropsWithDefaults = BadgeProps & typeof defaultProps;

const Styles = labelStyles('Badge', {
  root: (
    props: BadgePropsWithDefaults,
    badgeColorVariant: BadgeColorVariant
  ) => [
    {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative' as 'relative',
      zIndex: 1,

      /**
       * Linear-gradient backgrounds cannot be animated with `transition` natively
       * See: https://keithjgrant.com/posts/2017/07/transitioning-gradients/
       * This implementation taken from the above uses a pseudo-element to transition
       * the opacity property to simulate animating a linear-gradient. Still works fine
       * with non-linear-gradient backgrounds as well.
       */
      '&::before': {
        position: 'absolute' as 'absolute',
        content: '""',
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        // note: this must be the hover/focus background from the variant
        background: props.colorVariant
          ? props.colorVariant.hover.background
          : badgeColorVariant.hover.background,
        zIndex: -1,
        transition: Animation.transitionValue,
        opacity: 0,
        borderRadius: 20,
      },
    },
    responsiveStyles(props.size, {
      small: {
        ...Typography.altText900,
        height: 30,
        fontWeight: 600,
        borderRadius: 20,
        padding: '0 15px',
        '&::before': {
          borderRadius: 20,
        },
      },
      large: {
        ...Typography.subtitle900,
        height: 60,
        fontWeight: 700,
        borderRadius: 70,
        padding: '0 30px',
        '&::before': {
          borderRadius: 70,
        },
      },
    }),
    !props.colorVariant &&
      withModifier(props.color, {
        background: badgeColorVariant.base.background,
        color: badgeColorVariant.base.color,
      }),
    props.colorVariant &&
      withModifier('color-variant', {
        background: props.colorVariant.base.background,
        color: props.colorVariant.base.color,
      }),
    props.href &&
      withModifier('as-link', {
        '&:hover, &:focus': {
          color: props.colorVariant
            ? props.colorVariant.hover.color
            : badgeColorVariant.hover.color,
        },
        '&:hover::before, &:focus::before': {
          opacity: 1,
        },
      }),
  ],
});

/**
 * Renders a badge component. Used for breadcrumbs, etc.
 *
 * Tags: core-ui
 */
const Badge: React.FC<BadgeProps> = _props => {
  const props: BadgePropsWithDefaults = {
    ...defaultProps,
    ..._props,
  };
  const badgeVariant = BadgeVariants[props.color];
  const css = Styles.root(props, badgeVariant);
  const spanProps = {
    ...kebabCaseAriaAttrs(props),
    css,
  };
  const linkProps = {
    ...spanProps,
    target: props.hrefTarget,
    href: props.href as string,
  };
  return props.href !== undefined ? (
    <Link {...linkProps}>{props.children}</Link>
  ) : (
    <span {...spanProps}>{props.children}</span>
  );
};

Badge.defaultProps = defaultProps;

export default Badge;
